import React from "react";
import { usePreferences } from "../../context/PreferencesContext";
import { useTranslate } from "../../i18n/useTranslate";
import { LiveSection, Stat, Assumptions } from "./primitives";
import { HAZARD_ASSUMPTIONS } from "../../geo/services/multiHazard";
import { SERVICE_ASSUMPTIONS } from "../../geo/services/serviceStress";
import { MOBILITY_ASSUMPTIONS } from "../../geo/services/mobilityChain";

/* Operational engine sections — §9, §10, §11, §4.
 * Each is dropped at the top of the page that already covers that subject,
 * so the live figure leads and the page's original demo content follows. */

const useL = () => {
  const { lang } = usePreferences();
  return (o) => (typeof o === "string" ? o : o?.[lang] ?? o?.en ?? "");
};

/* ── §9 Multi-hazard correlation ───────────────────────────────────────── */
export function LiveHazardSection({ hazards }) {
  const t = useTranslate();
  const L = useL();
  const worst = hazards.sectors[0];

  return (
    <LiveSection
      title="Multi-Hazard Correlation"
      section="§9"
      note={hazards.coverage.statement}
    >
      <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Hazard types" value={hazards.coverage.hazardTypes} />
        <Stat label="Monitored" value={hazards.coverage.monitored} tone="green" />
        <Stat label="No data source" value={hazards.coverage.blind} tone="red" />
        <Stat
          label="Sectors amplified"
          value={hazards.amplified.length}
          tone={hazards.amplified.length ? "amber" : "slate"}
        />
      </div>

      {worst && (
        <div className="mb-3 rounded-xl border border-slate-300 bg-slate-50 p-3">
          <div className="mb-1 flex items-center gap-2">
            <span className="text-sm font-black text-slate-900">{L(worst.name)}</span>
            <span className={`rounded px-1.5 py-0.5 text-[9px] font-black uppercase ${
              worst.level === "severe" ? "bg-red-100 text-red-700"
              : worst.level === "elevated" ? "bg-orange-100 text-orange-700"
              : worst.level === "watch" ? "bg-amber-100 text-amber-700"
              : "bg-emerald-100 text-emerald-700"
            }`}>{worst.level}</span>
            <span className="ml-auto text-lg font-black tabular-nums text-slate-900">
              {Math.round(worst.compound * 100)}
            </span>
          </div>
          <p className="text-xs leading-snug text-slate-700">{worst.narrative}</p>

          <div className="mt-2 flex flex-wrap gap-1">
            {worst.hazards.map((h) => (
              <span
                key={h.key}
                title={h.reading}
                className={`rounded border px-1.5 py-0.5 text-[9px] font-bold tabular-nums ${
                  h.monitored === false ? "border-slate-300 bg-slate-100 text-slate-400 line-through"
                  : h.severity == null ? "border-slate-200 bg-white text-slate-400"
                  : h.severity >= 0.6 ? "border-red-300 bg-white text-red-700"
                  : h.severity >= 0.3 ? "border-amber-300 bg-white text-amber-700"
                  : "border-slate-200 bg-white text-slate-500"
                }`}
              >
                {L(h.label)} {h.severity == null ? "—" : Math.round(h.severity * 100)}
              </span>
            ))}
          </div>

          {worst.interactions[0] && (
            <p className="mt-2 border-t border-slate-200 pt-2 text-[11px] leading-snug text-slate-600">
              <strong>{t("Why it compounds")}:</strong> {worst.interactions[0].why}
            </p>
          )}
        </div>
      )}

      <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2">
        <div className="text-[9px] font-black uppercase tracking-wider text-slate-500">
          {t("Struck through above — no data source")}
        </div>
        <p className="text-[10px] leading-snug text-slate-600">
          {hazards.coverage.blindList.map((b) => b.label.en).join(" · ")}
        </p>
      </div>

      <Assumptions items={HAZARD_ASSUMPTIONS} />
    </LiveSection>
  );
}

/* ── §10 Service stress prediction ─────────────────────────────────────── */
export function LiveServiceSection({ services }) {
  const t = useTranslate();
  const L = useL();
  const notable = services.filter((r) => r.state !== "ok");
  const soonest = notable
    .filter((r) => r.minutesToStress != null)
    .sort((a, b) => a.minutesToStress - b.minutesToStress)[0];

  return (
    <LiveSection
      title="Service Stress Forecast"
      section="§10"
      note="A lead time, not a gauge: the projection states the arrival rate that produced it so an operator can argue with the arithmetic."
    >
      {soonest ? (
        <div className="mb-3 rounded-xl border border-amber-300 bg-amber-50 p-3">
          <div className="text-[10px] font-black uppercase tracking-wider text-amber-700">
            {t("Soonest threshold crossing")}
          </div>
          <p className="text-sm font-bold leading-snug text-amber-900">{soonest.narrative}</p>
        </div>
      ) : (
        <div className="mb-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
          {t("No service is predicted to cross its operational threshold within the next two hours.")}
        </div>
      )}

      <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Services tracked" value={7} />
        <Stat label="Service/sector pairs" value={services.length} />
        <Stat
          label="Under stress"
          value={notable.filter((r) => r.state === "stressed" || r.state === "failed").length}
          tone="red"
        />
        <Stat
          label="Approaching"
          value={notable.filter((r) => r.state === "approaching").length}
          tone="amber"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-200 text-left text-[10px] uppercase tracking-wide text-slate-400">
              <th className="pb-1 font-black">{t("Sector")}</th>
              <th className="pb-1 font-black">{t("Service")}</th>
              <th className="pb-1 text-right font-black">{t("Used")}</th>
              <th className="pb-1 text-right font-black">{t("Units")}</th>
              <th className="pb-1 text-right font-black">{t("Lead time")}</th>
            </tr>
          </thead>
          <tbody>
            {notable.slice(0, 8).map((r) => (
              <tr key={`${r.sectorId}-${r.service}`} className="border-b border-slate-100">
                <td className="py-1 text-slate-700">{L(r.sectorName)}</td>
                <td className="py-1 text-slate-700">{L(r.label)}</td>
                <td className="py-1 text-right tabular-nums text-slate-900">{r.utilisationPct}%</td>
                <td className="py-1 text-right tabular-nums text-slate-500">{r.units}</td>
                <td className="py-1 text-right">
                  {r.minutesToStress != null ? (
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-black tabular-nums text-amber-800">
                      T−{r.minutesToStress}m
                    </span>
                  ) : (
                    <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-black text-red-800">
                      {t("NOW")}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Assumptions items={SERVICE_ASSUMPTIONS} />
    </LiveSection>
  );
}

/* ── §11 Mobility chain ────────────────────────────────────────────────── */
export function LiveMobilitySection({ mobility }) {
  const t = useTranslate();
  const L = useL();
  const toGhat = mobility.propagation.filter((p) => p.toStage === "ghat").slice(0, 2);

  return (
    <LiveSection
      title="Mobility Chain"
      section="§11"
      note="Rail, bus and parking are parallel ingress channels, not a queue. A pilgrim arriving by train does not then use a car park."
    >
      <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Head demand" value={mobility.headDemandPerMin.toLocaleString("en-IN")} unit="/min" />
        <Stat label="Chain throughput" value={mobility.chainThroughputPerMin.toLocaleString("en-IN")} unit="/min" />
        <Stat
          label="Binding constraint"
          value={mobility.binding ? L(mobility.binding.label) : "—"}
          tone="amber"
        />
        <Stat label="Owning agency" value={mobility.binding?.agency ?? "—"} tone="indigo" />
      </div>

      {toGhat.map((p) => (
        <div
          key={`${p.fromStage}-${p.toStage}`}
          className={`mb-2 rounded-xl border p-3 ${
            p.effect === "shortfall" ? "border-sky-300 bg-sky-50" : "border-orange-300 bg-orange-50"
          }`}
        >
          <div className="mb-0.5 flex items-center gap-2 text-[10px] font-black uppercase tracking-wider">
            {p.effect === "shortfall" ? t("Downstream shortfall") : t("Downstream surge")}
            <span className="ml-auto tabular-nums">T+{p.inMinutes}m</span>
          </div>
          <p className="text-xs leading-snug text-slate-800">{p.narrative}</p>
        </div>
      ))}

      <div className="mt-2 grid grid-cols-2 gap-1.5 md:grid-cols-4">
        {mobility.stages.map((s) => (
          <div
            key={s.stage}
            className={`rounded-lg border px-2 py-1.5 ${
              s.state === "held" ? "border-slate-400 bg-slate-100"
              : s.state === "overloaded" ? "border-red-300 bg-red-50"
              : s.state === "saturated" ? "border-orange-300 bg-orange-50"
              : s.state === "tight" ? "border-amber-200 bg-amber-50"
              : "border-emerald-200 bg-emerald-50"
            }`}
          >
            <div className="flex items-center gap-1 text-[10px] font-bold text-slate-700">
              {s.parallel && <span className="text-[8px] text-slate-400">∥</span>}
              <span className="truncate">{L(s.label)}</span>
            </div>
            <div className="text-[13px] font-black tabular-nums text-slate-900">
              {s.utilisation == null ? t("HELD") : `${Math.round(s.utilisation * 100)}%`}
            </div>
            <div className="text-[9px] tabular-nums text-slate-500">
              {s.demandPerMin.toLocaleString("en-IN")} / {s.effectiveCapacityPerMin.toLocaleString("en-IN")}
            </div>
          </div>
        ))}
      </div>

      <Assumptions items={MOBILITY_ASSUMPTIONS} />
    </LiveSection>
  );
}

/* ── §4 Digital twin ───────────────────────────────────────────────────── */
export function LiveTwinSection({ twin, onInspect }) {
  const t = useTranslate();
  const L = useL();
  const counts = {};
  twin.all().forEach((n) => { counts[n.type] = (counts[n.type] ?? 0) + 1; });
  const gaps = twin.observationGaps();

  /* The question a map cannot answer, demonstrated rather than described. */
  const impact = twin.impactOf("BAR-01");

  return (
    <LiveSection
      title="Kumbh Digital Twin"
      section="§4"
      note="A typed graph, not a map layer. Every entity carries location, capacity, live state, relationships and a responsible agency."
    >
      <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Entities" value={twin.integrity.entityCount} />
        <Stat label="Entity types" value={twin.integrity.typeCount} />
        <Stat label="Dangling edges" value={twin.integrity.dangling.length} tone="green" />
        <Stat label="Sectors observed" value={`${gaps.filter((g) => g.observed).length}/${gaps.length}`} tone="red" />
      </div>

      {impact && (
        <div className="mb-3 rounded-xl border border-indigo-200 bg-indigo-50 p-3">
          <div className="mb-1 text-[10px] font-black uppercase tracking-wider text-indigo-700">
            {t("Impact traversal — what closing this affects")}
          </div>
          <p className="mb-1.5 text-xs text-slate-800">
            <strong>{L(impact.entity.name)}</strong> → {impact.affected.length}{" "}
            {t("dependent entities across")} {impact.agencies.length} {t("agencies")}
          </p>
          <div className="flex flex-wrap gap-1">
            {impact.affected.slice(0, 8).map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => onInspect?.(a)}
                className="rounded border border-indigo-300 bg-white px-1.5 py-0.5 text-[10px] font-medium text-indigo-800"
              >
                {L(a.name) || a.id}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-1">
        {Object.entries(counts)
          .sort((a, b) => b[1] - a[1])
          .map(([type, n]) => (
            <span key={type} className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] text-slate-600">
              {type} <strong className="tabular-nums text-slate-900">{n}</strong>
            </span>
          ))}
      </div>
    </LiveSection>
  );
}
